/**
 * AgentTimeline - Displays agent thinking, decisions, and tool calls
 *
 * Renders a timeline of agent steps: thinking (text) and sub-agent (collapsible tool calls)
 */

import { Brain, Wrench, Loader2 } from 'lucide-react';
import { Card, Collapsible } from '../common';
import type { AgentStep, ToolCallEntry } from '../../lib/agent-message-parser';

interface AgentTimelineProps {
  steps: AgentStep[];
  isLoading?: boolean;
  error?: string | null;
}

function ToolCallItem({ tool }: { tool: ToolCallEntry }) {
  return (
    <div className="flex items-start gap-2 py-1.5">
      <Wrench className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {tool.displayName}
        </span>
        {tool.inputSummary && (
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {tool.inputSummary}
          </p>
        )}
      </div>
    </div>
  );
}

function ThinkingStep({ step }: { step: AgentStep }) {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className="w-2 h-2 rounded-full bg-indigo-500 mt-2" />
        <div className="w-px flex-1 bg-gray-200 dark:bg-gray-700" />
      </div>
      <p className="text-sm text-gray-700 dark:text-gray-300 pb-4 leading-relaxed">
        {step.text}
      </p>
    </div>
  );
}

function SubAgentStep({ step }: { step: AgentStep }) {
  const toolCount = step.toolCalls.length;
  const toolLabel = `${toolCount} ${toolCount === 1 ? 'tool' : 'tools'}`;

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className="w-2 h-2 rounded-full bg-purple-500 mt-2" />
        <div className="w-px flex-1 bg-gray-200 dark:bg-gray-700" />
      </div>
      <div className="flex-1 pb-4">
        <Collapsible
          trigger={
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {step.subAgentName}
              </span>
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                {toolLabel}
              </span>
            </div>
          }
        >
          <div className="mt-2 ml-1 pl-3 border-l-2 border-gray-100 dark:border-gray-700">
            {step.toolCalls.map((tool) => (
              <ToolCallItem key={tool.id} tool={tool} />
            ))}
          </div>
        </Collapsible>
      </div>
    </div>
  );
}

export function AgentTimeline({ steps, isLoading, error }: AgentTimelineProps) {
  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
          <span className="text-sm text-gray-500 dark:text-gray-400">Loading agent activity...</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </Card>
    );
  }

  if (steps.length === 0) {
    return null;
  }

  return (
    <Card>
      <div className="flex items-center gap-3 mb-4">
        <Brain className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Agent Activity</h3>
      </div>
      <div>
        {steps.map((step) =>
          step.type === 'thinking' ? (
            <ThinkingStep key={step.id} step={step} />
          ) : (
            <SubAgentStep key={step.id} step={step} />
          )
        )}
      </div>
    </Card>
  );
}
